import Code from '@/components/dashboard/Code';
import ClientCode from '@/components/dashboard/ClientCode';
import SectionActions from '@/docs/SectionActions';
import Links from '@/docs/ExampleLinks';
import { useRef } from 'react'
import SEOMetadata from '@/components/SEOMetadata';
import DocHeading from '@/docs/DocHeading';

export default function ESGAPIDoc() {
  const sectionRef = useRef(null);
  return (
    <div className="api-documentation space-y-8 lg:max-w-[calc(100vw-340px)] md:max-w-[calc(100vw-80px)] max-w-[calc(100vw-30px)] overflow-hidden md:overflow-visible pb-50">
      <SEOMetadata title="ESG API" description="Get ESG scores with the ESG API. Access Environmental, Social, and Governance data for sustainable investing and corporate risk assessment."  canonicalUrl="https://axionquant.com/docs/02-alternative-data/ESG" />
      <div className="mb-10">
        <DocHeading className="text-3xl font-bold text-gray-900 mb-4">ESG API</DocHeading>
        <p className="text-lg text-gray-700 max-w-[900px]">
          The ESG API provides Environmental, Social, and Governance scores for publicly traded companies. These scores are essential for sustainable investing, risk assessment, and corporate responsibility analysis. Each category provides detailed metrics on different aspects of a company's sustainability performance.
        </p>
      </div>

      <div className="space-y-12" ref={sectionRef}>
        {/* Get ESG Scores by Ticker */}
        <div className="flex flex-col lg:flex-row lg:gap-8 max-w-[1600px]">
          <div className="flex-1 min-w-0 lg:pr-6 xl:pr-12">
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-4 mb-4">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                  GET
                </span>
                <code className="text-lg font-mono text-gray-800 bg-gray-50 px-3 py-1 rounded break-all">
                  /esg/:ticker
                </code>
              </div>
              <p className="text-gray-700 mb-6">
                Retrieves comprehensive ESG (Environmental, Social, and Governance) scores for a specific company. Returns scores for overall ESG performance as well as individual category scores. These metrics are critical for evaluating a company's sustainability practices, social impact, and corporate governance standards.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Path Parameters</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Parameter</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Required</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-4 py-3 text-sm font-mono text-gray-800">ticker</td>
                      <td className="px-4 py-3 text-sm text-gray-700">string</td>
                      <td className="px-4 py-3 text-sm">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        Stock ticker symbol of the company (e.g., &quot;AAPL&quot;, &quot;MSFT&quot;, &quot;GOOGL&quot;)
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <Links links={['/esg/AAPL', '/esg/MSFT']} />

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Response Fields</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">category <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">ESG category (e.g., &quot;esg&quot;, &quot;environment&quot;, &quot;social&quot;, &quot;governance&quot;, &quot;controversy&quot;)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">score <span className="text-gray-500">number</span></p>
                  <p className="text-sm text-gray-600">Numerical score on a 0-100 scale (higher is better for ESG categories; for controversy, lower indicates more significant issues)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">grade <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Letter grade equivalent (AAA, AA, A, BBB, BB, B, CCC, CC)</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <p className="font-mono text-sm text-gray-800 mb-1">id <span className="text-gray-500">string</span></p>
                  <p className="text-sm text-gray-600">Unique identifier for the score record</p>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-lg font-medium text-gray-900 mb-3">Scoring System</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <div className="text-center p-2 bg-gray-50 border border-gray-200 rounded">
                  <div className="font-semibold text-gray-800">AAA</div>
                  <div className="text-xs text-gray-600">90-100</div>
                </div>
                <div className="text-center p-2 bg-gray-50 border border-gray-200 rounded">
                  <div className="font-semibold text-gray-800">AA</div>
                  <div className="text-xs text-gray-600">80-89</div>
                </div>
                <div className="text-center p-2 bg-gray-50 border border-gray-200 rounded">
                  <div className="font-semibold text-gray-800">A</div>
                  <div className="text-xs text-gray-600">70-79</div>
                </div>
                <div className="text-center p-2 bg-gray-50 border border-gray-200 rounded">
                  <div className="font-semibold text-gray-800">BBB</div>
                  <div className="text-xs text-gray-600">50-69</div>
                </div>
                <div className="text-center p-2 bg-gray-50 border border-gray-200 rounded">
                  <div className="font-semibold text-gray-800">BB</div>
                  <div className="text-xs text-gray-600">30-49</div>
                </div>
                <div className="text-center p-2 bg-gray-50 border border-gray-200 rounded md:col-start-2">
                  <div className="font-semibold text-gray-800">B</div>
                  <div className="text-xs text-gray-600">20-29</div>
                </div>
                <div className="text-center p-2 bg-gray-50 border border-gray-200 rounded">
                  <div className="font-semibold text-gray-800">CCC</div>
                  <div className="text-xs text-gray-600">10-19</div>
                </div>
                <div className="text-center p-2 bg-gray-50 border border-gray-200 rounded">
                  <div className="font-semibold text-gray-800">CC</div>
                  <div className="text-xs text-gray-600">0-9</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="rounded-lg p-0 !pt-0 pb-4 lg:sticky lg:top-[12px]">
              <SectionActions sectionTitle="ESG Scores" contentRef={sectionRef} />

              <h3 className="text-sm font-semibold mb-3">ESG Scores</h3>
              <div className="mb-4">
                <p className="text-gray-400 text-xs mb-2">Request</p>
                <ClientCode id="esg/ticker"></ClientCode>
              </div>
              <div>
                <p className="text-gray-400 text-xs mb-2">Response</p>
                <Code id="02-alternative-data/ESG"></Code>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
